import { useRef, useState } from "react";
import { api } from "../../lib/api";

/**
 * <MediaInput />
 * Unified media field with two modes:
 *   - "Upload"        → drag/click → uploads to Bunny → fills value with CDN URL
 *   - "External link" → paste any URL (Vimeo, other CDN, …)
 *
 * Storage stays as a single string field on the parent — the toggle is purely UX.
 *
 * Props:
 *   value      string                    current URL
 *   onChange   (url:string) => void      called with the new value
 *   folder     string                    Bunny folder bucket: hero|works|blog|team|marketing|logo|misc
 *   accept     "image" | "video" | "any" client-side filter (default "any")
 *   testid     string                    forwarded as data-testid prefix on root
 *   placeholder string                   placeholder for the URL input
 *   compact    boolean                   tighter spacing for inline use
 */
export default function MediaInput({
  value = "",
  onChange,
  folder = "misc",
  accept = "any",
  testid = "media-input",
  placeholder = "https://…",
  compact = false,
}) {
  const [mode, setMode] = useState(value ? "link" : "upload");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const acceptAttr =
    accept === "image"
      ? "image/*"
      : accept === "video"
      ? "video/*"
      : accept === "audio"
      ? "audio/*"
      : "image/*,video/*,audio/*";

  const isImage = (url) => /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?|$)/i.test(url || "");
  const isVideo = (url) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || "");
  const isAudio = (url) => /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(url || "");
  const isVimeo = (url) => /(vimeo\.com|player\.vimeo\.com)/i.test(url || "");

  async function uploadFile(file) {
    setError("");
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      const { data } = await api.post("/uploads/bunny", fd, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      setMode("link"); // surface the URL
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 600);
    }
  }

  function onFilePicked(e) {
    const f = e.target.files?.[0];
    if (f) uploadFile(f);
    e.target.value = "";
  }

  function onDrop(e) {
    e.preventDefault();
    if (busy) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadFile(f);
  }

  const previewKind = isVimeo(value)
    ? "vimeo"
    : isVideo(value)
    ? "video"
    : isAudio(value)
    ? "audio"
    : isImage(value)
    ? "image"
    : value
    ? "link"
    : "none";

  return (
    <div data-testid={testid} className={compact ? "space-y-2" : "space-y-3"}>
      {/* Segmented mode toggle */}
      <div className="inline-flex border border-white/10 rounded-sm overflow-hidden text-[11px] uppercase tracking-[0.2em]">
        <button
          type="button"
          onClick={() => setMode("upload")}
          data-testid={`${testid}-mode-upload`}
          className={`px-3 py-1.5 transition-colors ${
            mode === "upload" ? "bg-[#C9A050] text-black" : "text-white/60 hover:text-white"
          }`}
        >
          Upload
        </button>
        <button
          type="button"
          onClick={() => setMode("link")}
          data-testid={`${testid}-mode-link`}
          className={`px-3 py-1.5 transition-colors border-l border-white/10 ${
            mode === "link" ? "bg-[#C9A050] text-black" : "text-white/60 hover:text-white"
          }`}
        >
          External link
        </button>
      </div>

      {/* Mode body */}
      {mode === "upload" ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => !busy && fileRef.current?.click()}
          data-testid={`${testid}-dropzone`}
          className={`cursor-pointer border border-dashed transition-colors ${
            busy ? "border-[#C9A050]/60 bg-[#C9A050]/5" : "border-white/15 hover:border-[#C9A050]/60 hover:bg-white/5"
          } rounded-sm px-4 py-5 text-center`}
        >
          <input
            ref={fileRef}
            type="file"
            accept={acceptAttr}
            onChange={onFilePicked}
            data-testid={`${testid}-file`}
            className="hidden"
          />
          {busy ? (
            <>
              <p className="text-xs text-[#C9A050] mb-2">Uploading… {progress}%</p>
              <div className="h-px w-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[#C9A050] transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-white/80">Drop file or click to upload</p>
              <p className="text-[11px] text-[#5A5A60] mt-1">
                {accept === "image"
                  ? "Images up to 25 MB"
                  : accept === "video"
                  ? "Videos up to 300 MB"
                  : accept === "audio"
                  ? "Audio up to 60 MB · mp3, wav, ogg, m4a, aac, flac"
                  : "Images 25 MB · Videos 300 MB · Audio 60 MB"}
              </p>
            </>
          )}
        </div>
      ) : (
        <input
          type="url"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testid}-url`}
          className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A050] outline-none px-0 py-2 text-sm text-white placeholder:text-[#5A5A60]"
        />
      )}

      {/* Error */}
      {error && (
        <p data-testid={`${testid}-error`} className="text-xs text-red-400">
          {error}
        </p>
      )}

      {/* Preview + clear */}
      {value && (
        <div className="flex items-start gap-3 pt-1">
          <div className="w-20 h-20 shrink-0 bg-[#121214] border border-white/10 overflow-hidden rounded-sm flex items-center justify-center">
            {previewKind === "image" ? (
              <img src={value} alt="" className="w-full h-full object-cover" />
            ) : previewKind === "video" ? (
              <video src={value} className="w-full h-full object-cover" muted playsInline />
            ) : previewKind === "vimeo" ? (
              <span className="text-[10px] text-[#C9A050] uppercase tracking-[0.2em]">Vimeo</span>
            ) : (
              <span className="text-[10px] text-[#8A8A93] uppercase tracking-[0.2em]">Link</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-[#8A8A93] truncate" title={value}>
              {value}
            </p>
            <button
              type="button"
              onClick={() => onChange?.("")}
              data-testid={`${testid}-clear`}
              className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-[#C9A050] transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
